import {
	Badge,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Skeleton,
} from "@ancatag/at-design";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, Clock, ShieldCheck, Zap } from "lucide-react";
import { type ReactNode, useState } from "react";
import { CacheHitChart } from "../components/charts/CacheHitChart";
import { LatencyChart } from "../components/charts/LatencyChart";
import { RequestsChart } from "../components/charts/RequestsChart";
import { TokenSavingsChart } from "../components/charts/TokenSavingsChart";
import { CreateProjectDialog } from "../components/projects/CreateProjectDialog";
import { ProjectEmptyState } from "../components/projects/ProjectEmptyState";
import { useAuth } from "../context/AuthContext";
import { useProjectContext } from "../context/ProjectContext";
import { useChartData, useRealTimeStats } from "../hooks/useAnalytics";
import { useAllApiKeyMetrics } from "../hooks/useApiKeys";
import { listMyRouteConfigs } from "../services/route-configs";

export function Analytics() {
	const {
		selectedProject,
		isLoading: projectLoading,
		refreshProjects,
	} = useProjectContext();
	const { user } = useAuth();
	const isSuperAdmin = user?.role === "superAdmin";
	const [period, setPeriod] = useState<"hour" | "day" | "week" | "month">(
		"day",
	);
	const [modelConfigId, setModelConfigId] = useState<string | undefined>(
		undefined,
	);
	const [createProjectOpen, setCreateProjectOpen] = useState(false);

	// Fetch model configs for filter dropdown
	const { data: modelConfigs } = useQuery({
		queryKey: ["model-configs", selectedProject?.id],
		queryFn: () => listMyRouteConfigs({ page: 1, limit: 100 }),
		enabled: Boolean(selectedProject?.id),
	});

	const connectedModelConfigs =
		modelConfigs?.data?.filter((c) => {
			const primaryModel = c.models?.find((m) => m.isPrimary)?.modelConfig;
			return primaryModel?.connectionStatus === "connected";
		}) || [];

	// Real-time stats (polls every 10s)
	const { data: realTimeStats, isLoading: realTimeLoading } =
		useRealTimeStats(modelConfigId);

	// Chart data
	const { data: chartData, isLoading: chartLoading } = useChartData(
		period,
		modelConfigId,
	);

	// Legacy metrics for summary stats
	const { data: metrics, isLoading: metricsLoading } =
		useAllApiKeyMetrics(period);

	if (projectLoading) {
		return <AnalyticsSkeleton />;
	}

	if (!selectedProject && !isSuperAdmin) {
		return (
			<div className="space-y-6">
				<h1 className="text-3xl font-bold text-foreground">
					Usage & Analytics
				</h1>
				<p className="text-muted-foreground">
					Select or create a project to view usage, cache savings, and latency
					trends.
				</p>
				<ProjectEmptyState
					onCreateProject={() => setCreateProjectOpen(true)}
					onRefresh={refreshProjects}
				/>
				<CreateProjectDialog
					open={createProjectOpen}
					onOpenChange={setCreateProjectOpen}
				/>
			</div>
		);
	}

	const summary = metrics?.summary;
	const isLoading = metricsLoading || chartLoading;

	return (
		<div className="space-y-6">
			<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
				<div>
					<h1 className="text-2xl md:text-3xl font-bold text-foreground">
						Usage & Analytics
					</h1>
					<p className="text-muted-foreground text-sm md:text-base">
						{isSuperAdmin
							? "Platform-wide insights and analytics"
							: `Project insights for ${selectedProject?.name || "your project"}`}
					</p>
				</div>
				<div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
					<div className="flex items-center gap-2">
						<Badge variant="secondary">Period</Badge>
						<Select
							value={period}
							onValueChange={(value) =>
								setPeriod(value as "hour" | "day" | "week" | "month")
							}
						>
							<SelectTrigger className="w-full sm:w-[180px] h-10">
								<SelectValue placeholder="Select period" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="hour">Last 24 hours (hourly)</SelectItem>
								<SelectItem value="day">Last 7 days (daily)</SelectItem>
								<SelectItem value="week">Last 6 weeks</SelectItem>
								<SelectItem value="month">Last 6 months</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<div className="flex items-center gap-2">
						<Badge variant="secondary">Model</Badge>
						<Select
							value={modelConfigId || "all"}
							onValueChange={(value) =>
								setModelConfigId(value === "all" ? undefined : value)
							}
						>
							<SelectTrigger className="w-full sm:w-[200px] h-10">
								<SelectValue placeholder="All Models" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All Models</SelectItem>
								{connectedModelConfigs.map((config) => (
									<SelectItem key={config.id} value={config.id}>
										{config.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>
			</div>

			{/* Real-time Stats Row */}
			<div className="grid grid-cols-1 md:grid-cols-4 gap-4">
				<StatCard
					title="Requests (5min)"
					icon={<BarChart3 className="w-4 h-4" />}
					value={
						realTimeLoading
							? "—"
							: realTimeStats?.totalRequests?.toLocaleString() || "0"
					}
					subtitle={
						realTimeStats ? `${realTimeStats.errors} errors` : undefined
					}
				/>
				<StatCard
					title="Cache Hit Rate"
					icon={<ShieldCheck className="w-4 h-4" />}
					value={
						realTimeLoading
							? "—"
							: `${realTimeStats?.cacheHitRate?.toFixed(1) || "0.0"}%`
					}
					subtitle={
						realTimeStats ? `${realTimeStats.cacheHits} hits` : undefined
					}
				/>
				<StatCard
					title="Avg Latency"
					icon={<Clock className="w-4 h-4" />}
					value={
						realTimeLoading
							? "—"
							: realTimeStats?.avgLatencyMs
								? `${realTimeStats.avgLatencyMs.toFixed(0)} ms`
								: "—"
					}
				/>
				<StatCard
					title="Tokens Saved"
					icon={<Zap className="w-4 h-4" />}
					value={
						realTimeLoading
							? "—"
							: realTimeStats?.tokensSaved?.toLocaleString() || "0"
					}
				/>
			</div>

			{/* Historical Summary Stats */}
			{summary && (
				<div className="grid grid-cols-1 md:grid-cols-4 gap-4">
					<StatCard
						title="Total Requests"
						icon={<BarChart3 className="w-4 h-4" />}
						value={summary?.totalRequests?.toLocaleString() || "0"}
						subtitle={`Period: ${period}`}
					/>
					<StatCard
						title="Cache Hit Rate"
						icon={<ShieldCheck className="w-4 h-4" />}
						value={`${summary?.cacheHitRate?.toFixed(1) || "0.0"}%`}
						subtitle={`${summary.cacheHits} hits`}
					/>
					<StatCard
						title="Avg Latency"
						icon={<Clock className="w-4 h-4" />}
						value={
							summary?.avgLatencyMs
								? `${summary.avgLatencyMs.toFixed(0)} ms`
								: "—"
						}
					/>
					<StatCard
						title="Tokens Saved"
						icon={<Zap className="w-4 h-4" />}
						value={summary?.tokensSaved?.toLocaleString() || "0"}
					/>
				</div>
			)}

			{/* Charts Grid */}
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
				<RequestsChart
					data={chartData?.timeSeries || []}
					isLoading={isLoading}
				/>
				<CacheHitChart
					data={chartData?.timeSeries || []}
					isLoading={isLoading}
				/>
				<LatencyChart
					data={chartData?.timeSeries || []}
					isLoading={isLoading}
				/>
				<TokenSavingsChart
					data={chartData?.timeSeries || []}
					isLoading={isLoading}
				/>
			</div>
		</div>
	);
}

function StatCard({
	title,
	value,
	icon,
	subtitle,
}: {
	title: string;
	value: string;
	icon: ReactNode;
	subtitle?: string;
}) {
	return (
		<Card>
			<CardHeader className="pb-2">
				<CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
					{icon}
					{title}
				</CardTitle>
			</CardHeader>
			<CardContent>
				<div className="text-2xl font-bold">{value}</div>
				{subtitle && (
					<div className="text-xs text-muted-foreground mt-1">{subtitle}</div>
				)}
			</CardContent>
		</Card>
	);
}

function AnalyticsSkeleton() {
	return (
		<div className="space-y-4">
			<Skeleton className="h-8 w-64" />
			<div className="grid grid-cols-1 md:grid-cols-4 gap-4">
				{[1, 2, 3, 4].map((i) => (
					<Skeleton key={i} className="h-24" />
				))}
			</div>
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
				<Skeleton className="h-80" />
				<Skeleton className="h-80" />
				<Skeleton className="h-80" />
				<Skeleton className="h-80" />
			</div>
		</div>
	);
}

export default Analytics;
